import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail } from 'lucide-react';
import Logo from './Logo';
import { telehealthServices } from '../data/telehealthServices';
import { SITE_NAME } from '../siteConfig';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About us', path: '/about' },
    { name: 'Services & pricing', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  const featuredServices = telehealthServices.map((s) => ({
    name: s.navLabel,
    path: `/services/${s.slug}`,
  }));

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <motion.div
            className="footer-section company-info"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="footer-logo">
              <Logo size="large" />
            </div>
            <p className="company-description">
              {SITE_NAME} delivers compassionate telehealth care—sick visits, refills, labs, chronic support, weight
              management, and smoking cessation—with transparent fees for our virtual services.
            </p>
          </motion.div>

          <motion.div
            className="footer-section quick-links"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4>Quick links</h4>
            <ul>
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="footer-link">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="footer-section featured-services"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4>Services</h4>
            <ul>
              {featuredServices.map((service, index) => (
                <li key={index}>
                  <Link to={service.path} className="footer-link">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="footer-section contact-info"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4>Contact</h4>
            <div className="contact-details">
              <div className="contact-item">
                <MapPin className="contact-icon" />
                <div className="contact-text">
                  <p>{SITE_NAME}</p>
                  <p>Telehealth services — reach us remotely.</p>
                </div>
              </div>
              <div className="contact-item">
                <Phone className="contact-icon" />
                <div className="contact-text">
                  <p>Call to schedule or ask questions.</p>
                </div>
              </div>
              <div className="contact-item">
                <Mail className="contact-icon" />
                <div className="contact-text">
                  <p>Use our contact form or email when provided.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="footer-bottom-content">
            <p className="copyright">
              Copyright © {currentYear} <span className="company-name">{SITE_NAME}</span> – All rights reserved.
            </p>
            <p className="footer-note">
              Telehealth is not for emergencies. If you are experiencing an emergency, call your local emergency number.
            </p>
          </div>

          <motion.button
            type="button"
            className="scroll-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Scroll to top"
          >
            <span>↑</span>
            <span>Scroll to Top</span>
          </motion.button>
        </motion.div>
      </div>

      <div className="footer-bg-elements">
        <motion.div
          className="footer-bg-element footer-bg-1"
          animate={{
            y: [0, -10, 0],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="footer-bg-element footer-bg-2"
          animate={{
            y: [0, 8, 0],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
      </div>
    </footer>
  );
};

export default Footer;
